/**
 * The guest TEXT-chat switch (configs/ai/customer-chat.json).
 *
 * Deliberately NOT part of `configs/ai/customer-voice.json`. Voice and text
 * chat are off for different reasons, and one switch per reason is the whole
 * point: flipping voice back on must not resurrect the chat pill, and this
 * switch must not touch the microphone. Owner AI is a third switch for the
 * same reason.
 *
 * Edit the JSON, never this wrapper (AGENTS.md config rules).
 */

import customerChatConfig from '@/configs/ai/customer-chat.json';

export interface CustomerChatConfig {
  /** Master switch. false = the pill renders nothing and the turn route refuses. */
  enabled: boolean;
  status: string;
  switchedOffAt?: string;
  rationale: string[];
  gatedRoutes: string[];
  gatedLibraries: string[];
  uiEntryPoints: string[];
  uiEnforcement: string;
  restoreNote?: string;
  knownDependencies?: string[];
}

const raw = customerChatConfig as unknown as Record<string, unknown>;

function readStringArray(key: string): string[] {
  const value = raw[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Defaults to the OFF state, fail-closed, for the same reason as
 * `ownerAiEnabled()`: a config that fails to load must not be the reason a
 * guest ends up on a live assistant path that was meant to be closed.
 */
export const customerChat: CustomerChatConfig = {
  enabled: raw.enabled === true,
  status: typeof raw.status === 'string' ? raw.status : 'coming_soon',
  switchedOffAt: typeof raw.switchedOffAt === 'string' ? raw.switchedOffAt : undefined,
  rationale: readStringArray('rationale'),
  gatedRoutes: readStringArray('gatedRoutes'),
  gatedLibraries: readStringArray('gatedLibraries'),
  uiEntryPoints: readStringArray('uiEntryPoints'),
  uiEnforcement: typeof raw.uiEnforcement === 'string' ? raw.uiEnforcement : '',
  restoreNote: typeof raw.restoreNote === 'string' ? raw.restoreNote : undefined,
  knownDependencies: readStringArray('knownDependencies'),
};

/** Is guest text chat on? */
export function customerChatEnabled(): boolean {
  return customerChat.enabled;
}

/** Why chat is refused, for the 403 body and for logs. */
export function customerChatDisabledReason(): string {
  return 'The menu assistant is switched off right now. You can browse, order and pay without it.';
}
